import { useId, useMemo, useState } from "react";

import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";

/**
 * Two charts, drawn as SVG, with no charting library.
 *
 * That is a decision rather than stubbornness. Recharts is roughly 400 kB before
 * gzip and would be pulled into a bundle that is downloaded over a prepaid data
 * bundle on a phone in a township — for two chart shapes with no interaction
 * beyond a tooltip. The whole of this file is under 200 lines and ships as part
 * of the app.
 *
 * The constraint it accepts: these are the only two shapes. A third kind of
 * chart is a reason to reconsider, not a reason to extend this file until it is
 * a charting library nobody chose.
 *
 * **Values arrive as strings and stay strings.** The server sends money as
 * decimal strings so nothing is lost to float; converting to `Number` here is
 * for geometry only — pixel positions — and every figure a person reads is
 * formatted from the original string.
 */

export interface ChartPoint {
  /** The x-axis label. Rendered as given. */
  label: string;
  /** A decimal string, as the server sent it. */
  value: string;
  /** Optional second series, drawn behind the first. */
  secondary?: string;
}

export interface ChartProps {
  points: ChartPoint[];
  /** Accessible description. Required — a chart with no text is unreadable to a screen reader. */
  caption: string;
  className?: string;
  height?: number;
}

const PAD = { top: 8, right: 8, bottom: 22, left: 8 };

/**
 * Takings over time.
 *
 * An area chart rather than a line, because the shape of the month is what a
 * shopkeeper reads at a glance and the fill makes it legible on a phone in
 * daylight.
 */
export function TrendChart({ points, caption, className, height = 160 }: ChartProps) {
  const id = useId();
  const [hovered, setHovered] = useState<number | null>(null);

  const geometry = useMemo(() => {
    const values = points.map((point) => Number(point.value) || 0);
    // A flat series must not divide by zero, and a series of zeroes should draw
    // a flat line along the bottom rather than through the middle.
    const peak = Math.max(...values, 0.01);
    const width = 100;
    const usable = height - PAD.top - PAD.bottom;

    const coordinates = values.map((value, index) => ({
      x: points.length === 1 ? width / 2 : (index / (points.length - 1)) * width,
      y: PAD.top + usable - (value / peak) * usable,
    }));

    return { coordinates, peak, width, usable };
  }, [points, height]);

  if (points.length === 0) {
    return <EmptyChart caption={caption} height={height} className={className} />;
  }

  const { coordinates, width } = geometry;
  const line = coordinates.map((point, index) => `${index === 0 ? "M" : "L"}${point.x} ${point.y}`);
  const area = [
    ...line,
    `L${coordinates[coordinates.length - 1]!.x} ${height - PAD.bottom}`,
    `L${coordinates[0]!.x} ${height - PAD.bottom}`,
    "Z",
  ];

  const active = hovered === null ? null : points[hovered];

  return (
    <figure className={cn("relative", className)}>
      <figcaption className="sr-only">{caption}</figcaption>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="w-full"
        style={{ height }}
        role="img"
        aria-label={caption}
      >
        <defs>
          <linearGradient id={`${id}-fill`} x1="0" y1="0" x2="0" y2="1">
            {/* The CSS variable directly, not `theme(colors.brand.500)`: the
                palette is defined as `rgb(var(--ss-green-500) / <alpha-value>)`,
                and `theme()` would emit that literally — `<alpha-value>` is not
                valid CSS, so the gradient would silently not paint. */}
            <stop offset="0%" stopColor="rgb(var(--ss-green-500))" stopOpacity="0.28" />
            <stop offset="100%" stopColor="rgb(var(--ss-green-500))" stopOpacity="0" />
          </linearGradient>
        </defs>

        <path d={area.join(" ")} fill={`url(#${id}-fill)`} />
        <path
          d={line.join(" ")}
          fill="none"
          className="stroke-brand-500"
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
        />

        {hovered !== null && coordinates[hovered] && (
          <circle
            cx={coordinates[hovered].x}
            cy={coordinates[hovered].y}
            r="2.5"
            className="fill-brand-600"
            vectorEffect="non-scaling-stroke"
          />
        )}

        {/* One transparent band per point, so a finger on a phone hits something
            the width of a column rather than a 1.5 px line. */}
        {points.map((point, index) => (
          <rect
            key={point.label}
            x={(index / points.length) * width}
            y={0}
            width={width / points.length}
            height={height}
            fill="transparent"
            onMouseEnter={() => setHovered(index)}
            onMouseLeave={() => setHovered(null)}
          />
        ))}
      </svg>

      <div className="flex justify-between px-1 text-[0.6875rem] text-ink-500">
        <span>{points[0]!.label}</span>
        {points.length > 1 && <span>{points[points.length - 1]!.label}</span>}
      </div>

      {active && (
        <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 rounded-control border border-line bg-surface px-2 py-1 text-xs shadow-sm">
          <span className="font-medium text-ink-900">{formatMoney(active.value)}</span>
          <span className="ml-1.5 text-ink-500">{active.label}</span>
        </div>
      )}
    </figure>
  );
}

/**
 * A category comparison — hours of the day, payment methods, top products.
 *
 * Horizontal bars rather than vertical: the labels are words ("Coca-Cola
 * 500ml"), and words read along a row without being rotated forty-five degrees.
 */
export function BarChart({
  points,
  caption,
  className,
  formatValue = formatMoney,
}: Omit<ChartProps, "height"> & { formatValue?: (value: string) => string }) {
  const peak = Math.max(...points.map((point) => Number(point.value) || 0), 0.01);

  if (points.length === 0) {
    return <EmptyChart caption={caption} height={120} className={className} />;
  }

  return (
    <figure className={cn("space-y-1.5", className)}>
      <figcaption className="sr-only">{caption}</figcaption>

      {points.map((point) => {
        const share = ((Number(point.value) || 0) / peak) * 100;
        return (
          <div key={point.label} className="flex items-center gap-2 text-xs">
            <span className="w-28 shrink-0 truncate text-ink-600" title={point.label}>
              {point.label}
            </span>
            <span className="h-4 flex-1 overflow-hidden rounded-sm bg-surface-2">
              <span
                className="block h-full rounded-sm bg-brand-500/80"
                style={{ width: `${Math.max(share, 1)}%` }}
              />
            </span>
            <span className="w-20 shrink-0 text-right tabular-nums text-ink-900">
              {formatValue(point.value)}
            </span>
          </div>
        );
      })}
    </figure>
  );
}

function EmptyChart({
  caption,
  height,
  className,
}: {
  caption: string;
  height: number;
  className?: string | undefined;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-control border border-dashed border-line text-sm text-ink-500",
        className,
      )}
      style={{ height }}
    >
      Nothing to show yet
      <span className="sr-only"> — {caption}</span>
    </div>
  );
}
